import { Document, Page, StyleSheet, Font, View, Text, Image } from '@react-pdf/renderer'
import { FC } from 'react'

Font.register({
  family: 'myriad-pro',
  fonts: [
    {
      src: require('../../../../../assets/fonts/myriad-pro-regular.woff'),
      fontWeight: 400
    },
    {
      src: require('../../../../../assets/fonts/myriad-pro-semibold.woff'),
      fontWeight: 600
    },
    {
      src: require('../../../../../assets/fonts/myriad-pro-bold.woff'),
      fontWeight: 700
    }
  ]
})

Font.registerHyphenationCallback(word => [word])

export interface LogoData {
  src: string
  width: number
  height: number
}

interface AIPlanTitlePagePdfProps {
  organizationName: string
  accountName: string
  agencyLogo?: LogoData
  clientLogo?: LogoData
  isDirect?: boolean
}

const PREPARED_FOR_BOTTOM = 5.09
const BOTTOM_TEXT_TOP = 7.47
const BOTTOM_TEXT_BOTTOM = 7.75

const ELEMENT_SPACING = 0.35
const LOGO_MAX_WIDTH = 3.5
const AGENCY_MAX_HEIGHT = 1.25
const LOGO_TARGET_AREA = 3.0

const CLIENT_GAP = BOTTOM_TEXT_TOP - PREPARED_FOR_BOTTOM
const CLIENT_MAX_HEIGHT = CLIENT_GAP - 3 * ELEMENT_SPACING
const CLIENT_GAP_CENTER = (PREPARED_FOR_BOTTOM + BOTTOM_TEXT_TOP) / 2
const AGENCY_LOGO_TOP = BOTTOM_TEXT_BOTTOM + ELEMENT_SPACING

interface LogoLayout {
  top: string
  style: { width: string; height: string }
}

const fitToArea = (imgW: number, imgH: number, maxW: number, maxH: number, targetArea: number) => {
  if (imgW <= 0 || imgH <= 0) return { width: maxW, height: maxH }
  const aspect = imgW / imgH
  let h = Math.sqrt(targetArea / aspect)
  let w = aspect * h
  if (w > maxW) { w = maxW; h = w / aspect }
  if (h > maxH) { h = maxH; w = aspect * h }
  return { width: w, height: h }
}

const calculateLogoLayout = (clientLogo?: LogoData, agencyLogo?: LogoData) => {
  let client: LogoLayout | null = null
  let agency: LogoLayout | null = null

  if (clientLogo) {
    const fit = fitToArea(clientLogo.width, clientLogo.height, LOGO_MAX_WIDTH, CLIENT_MAX_HEIGHT, LOGO_TARGET_AREA)
    client = {
      top: `${CLIENT_GAP_CENTER - fit.height / 2}in`,
      style: { width: `${fit.width}in`, height: `${fit.height}in` }
    }
  }

  if (agencyLogo) {
    const fit = fitToArea(agencyLogo.width, agencyLogo.height, LOGO_MAX_WIDTH, AGENCY_MAX_HEIGHT, LOGO_TARGET_AREA)
    agency = {
      top: `${AGENCY_LOGO_TOP}in`,
      style: { width: `${fit.width}in`, height: `${fit.height}in` }
    }
  }

  return { client, agency }
}

const styles = StyleSheet.create({
  page: {
    backgroundColor: '#ffffff',
    fontFamily: 'myriad-pro'
  },
  textLayer: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '8.5in',
    height: '11in',
    flexDirection: 'column' as const,
    paddingTop: '2.625in',
    paddingLeft: '1.75in',
    paddingBottom: '1.25in',
    paddingRight: '1.75in',
    fontFamily: 'myriad-pro',
    fontSize: '12pt',
    lineHeight: 1.6,
    wordWrap: 'break-word',
    maxWidth: '100%',
    textAlign: 'center'
  },
  header: {
    fontFamily: 'myriad-pro',
    fontSize: '26pt',
    lineHeight: 1.2,
    marginBottom: '1.75in',
    fontWeight: 600,
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  sectionSubtitle: {
    fontFamily: 'myriad-pro',
    fontSize: '17pt',
    lineHeight: 1.2,
    fontWeight: 500,
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  logoContainer: {
    position: 'absolute' as const,
    left: '1.75in',
    right: '1.75in',
    alignItems: 'center' as const,
    padding: '4pt'
  }
})

const DEFAULT_CLIENT_TOP = '5.5in'
const DEFAULT_AGENCY_TOP = '8.1in'

const AIPlanTitlePagePdf: FC<AIPlanTitlePagePdfProps> = ({ organizationName, accountName, agencyLogo, clientLogo, isDirect }) => {
  const layout = calculateLogoLayout(clientLogo, agencyLogo)
  const bottomText = isDirect ? 'Generated by AdCritter' : `A Client of ${organizationName}`

  return (
    <Document>
      <Page size='LETTER' style={styles.page}>
        <View style={styles.textLayer}>
          <Text style={styles.header}>Comprehensive Media Plan</Text>
          <View style={{ marginBottom: '2.375in' }}>
            <Text style={styles.sectionSubtitle}>Prepared for {accountName}</Text>
          </View>
          <Text style={styles.sectionSubtitle}>{bottomText}</Text>
        </View>

        <View style={{ ...styles.logoContainer, top: layout.client?.top ?? DEFAULT_CLIENT_TOP }}>
          {clientLogo && layout.client && <Image src={clientLogo.src} style={layout.client.style} />}
        </View>

        <View style={{ ...styles.logoContainer, top: layout.agency?.top ?? DEFAULT_AGENCY_TOP }}>
          {agencyLogo && layout.agency && <Image src={agencyLogo.src} style={layout.agency.style} />}
        </View>
      </Page>
    </Document>
  )
}

export default AIPlanTitlePagePdf
